/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import { getAccountMetaFactory, type ResolvedAccount } from "../shared";

export const FILE_DISPUTE_DISCRIMINATOR = new Uint8Array([
  210, 63, 221, 114, 212, 97, 195, 156,
]);

export function getFileDisputeDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    FILE_DISPUTE_DISCRIMINATOR,
  );
}

export type FileDisputeInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountEscrow extends string | AccountMeta<string> = string,
  TAccountClient extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountEscrow extends string
        ? WritableAccount<TAccountEscrow>
        : TAccountEscrow,
      TAccountClient extends string
        ? ReadonlySignerAccount<TAccountClient> &
            AccountSignerMeta<TAccountClient>
        : TAccountClient,
      ...TRemainingAccounts,
    ]
  >;

export type FileDisputeInstructionData = {
  discriminator: ReadonlyUint8Array;
  reason: string;
};

export type FileDisputeInstructionDataArgs = { reason: string };

export function getFileDisputeInstructionDataEncoder(): Encoder<FileDisputeInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["reason", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
    ]),
    (value) => ({ ...value, discriminator: FILE_DISPUTE_DISCRIMINATOR }),
  );
}

export function getFileDisputeInstructionDataDecoder(): Decoder<FileDisputeInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["reason", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
  ]);
}

export function getFileDisputeInstructionDataCodec(): Codec<
  FileDisputeInstructionDataArgs,
  FileDisputeInstructionData
> {
  return combineCodec(
    getFileDisputeInstructionDataEncoder(),
    getFileDisputeInstructionDataDecoder(),
  );
}

export type FileDisputeInput<
  TAccountEscrow extends string = string,
  TAccountClient extends string = string,
> = {
  escrow: Address<TAccountEscrow>;
  client: TransactionSigner<TAccountClient>;
  reason: FileDisputeInstructionDataArgs["reason"];
};

export function getFileDisputeInstruction<
  TAccountEscrow extends string,
  TAccountClient extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: FileDisputeInput<TAccountEscrow, TAccountClient>,
  config?: { programAddress?: TProgramAddress },
): FileDisputeInstruction<TProgramAddress, TAccountEscrow, TAccountClient> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    escrow: { value: input.escrow ?? null, isWritable: true },
    client: { value: input.client ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.escrow),
      getAccountMeta(accounts.client),
    ],
    data: getFileDisputeInstructionDataEncoder().encode(
      args as FileDisputeInstructionDataArgs,
    ),
    programAddress,
  } as FileDisputeInstruction<TProgramAddress, TAccountEscrow, TAccountClient>);
}

export type ParsedFileDisputeInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    escrow: TAccountMetas[0];
    client: TAccountMetas[1];
  };
  data: FileDisputeInstructionData;
};

export function parseFileDisputeInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedFileDisputeInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { escrow: getNextAccount(), client: getNextAccount() },
    data: getFileDisputeInstructionDataDecoder().decode(instruction.data),
  };
}
